import { Account } from "models/Account";
import { Project } from "models/Project";
import React, { useMemo } from "react";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface ResponsibleHoursChartProps {
    accountById: Map<string, Account>;
    projects: Project[];
    workerId: string;
}

export interface ResponsibleHoursSlice {
    responsibleId: string;
    minutes: number;
}

const unassignedResponsibleId = "__unassigned__";
const chartColors = [
    "#556ee6",
    "#34c38f",
    "#f1b44c",
    "#f46a6a",
    "#50a5f1",
    "#74788d",
    "#6f42c1",
    "#e83e8c",
];

/** Project hours belong to the project's primary responsible, matching the project editor. */
export const responsibleHoursForWorker = (
    projects: Project[],
    workerId: string,
): ResponsibleHoursSlice[] => {
    const minutesByResponsible = new Map<string, number>();

    projects.forEach(project => {
        const worker = project.workers.find(item => item.account_id === workerId);
        worker?.entries?.forEach(entry => {
            if (entry.minutes <= 0) return;
            const responsibleId = project.responsible[0] || unassignedResponsibleId;
            minutesByResponsible.set(
                responsibleId,
                (minutesByResponsible.get(responsibleId) ?? 0) + entry.minutes,
            );
        });
    });

    return Array.from(minutesByResponsible, ([responsibleId, minutes]) => ({
        responsibleId,
        minutes,
    })).sort((first, second) => second.minutes - first.minutes ||
        first.responsibleId.localeCompare(second.responsibleId));
};

const ResponsibleHoursChart: React.FC<ResponsibleHoursChartProps> = ({
    accountById,
    projects,
    workerId,
}) => {
    const { intlLocale, t } = useOfficeTranslation();
    const slices = useMemo(
        () => responsibleHoursForWorker(projects, workerId),
        [projects, workerId],
    );
    const totalMinutes = slices.reduce((total, slice) => total + slice.minutes, 0);
    const percentage = (minutes: number) => new Intl.NumberFormat(intlLocale, {
        maximumFractionDigits: 1,
    }).format(minutes / totalMinutes * 100);
    const hours = (minutes: number) => new Intl.NumberFormat(intlLocale, {
        maximumFractionDigits: 1,
    }).format(minutes / 60);
    const responsibleName = (responsibleId: string) => responsibleId === unassignedResponsibleId
        ? t("accounts.accounts.noResponsible")
        : accountById.get(responsibleId)?.legal_name ?? responsibleId;
    const title = t("accounts.accounts.clientDistribution");
    const chartDescription = totalMinutes > 0
        ? `${title}: ${slices.map(slice => t("accounts.accounts.clientDistributionShare", {
            name: responsibleName(slice.responsibleId),
            percentage: percentage(slice.minutes),
            hours: hours(slice.minutes),
        })).join(", ")}`
        : `${title}: ${t("accounts.accounts.noHoursRegistered")}`;

    let offset = 0;
    const gradient = slices.map((slice, index) => {
        const start = offset;
        offset += slice.minutes / totalMinutes * 100;
        return `${chartColors[index % chartColors.length]} ${start}% ${offset}%`;
    }).join(", ");

    return <div className="d-flex align-items-center gap-2" aria-label={chartDescription} role="img">
        <div
            aria-hidden="true"
            className="flex-shrink-0 rounded-circle"
            style={{
                background: totalMinutes > 0 ? `conic-gradient(${gradient})` : "#e9ecef",
                height: 52,
                width: 52,
            }}
        />
        <div className="small overflow-hidden">
            <span className="d-block fw-semibold text-muted">{title}</span>
            {totalMinutes === 0 && <span className="text-muted">{t("accounts.accounts.noHoursRegistered")}</span>}
            {slices.map((slice, index) => <span className="d-flex align-items-start gap-1" key={slice.responsibleId}>
                <i
                    aria-hidden="true"
                    className="mdi mdi-circle flex-shrink-0"
                    style={{ color: chartColors[index % chartColors.length] }}
                />
                <span className="text-truncate" title={responsibleName(slice.responsibleId)}>
                    {responsibleName(slice.responsibleId)}
                    <span className="d-block text-muted">
                        {percentage(slice.minutes)}% · {t("accounts.accounts.hoursShort", { hours: hours(slice.minutes) })}
                    </span>
                </span>
            </span>)}
        </div>
    </div>;
};

export default ResponsibleHoursChart;
